/**
 * Shared helpers for the seller "My listings" views.
 * Kept free of JSX so components can import them without breaking fast refresh.
 */

export type SellerStatusGroup = "Active" | "Pending" | "Sold" | "Withdrawn";

/** MKT-43: held for the inviting company before going to every buyer */
export const isFirstLookStatus = (status: string | null | undefined): boolean =>
  (status ?? "").toUpperCase().replace(/[\s_]+/g, " ") === "FIRST LOOK";

/**
 * After the company passes, the listing is held a short while before buyers see it, but the
 * seller is told it is live (Tony, Oct 31), so it shows as an ordinary Active listing.
 */
export const sellerFacingStatus = (
  status: string | null | undefined,
  firstLookState: string | null | undefined,
): string | null | undefined => (isFirstLookStatus(status) && firstLookState === "pass_hold" ? "Active" : status);

/** Collapse the backend's many listing statuses into the four a seller manages */
export const normalizeListingStatus = (status: string | null | undefined): SellerStatusGroup | string => {
  const s = status?.toUpperCase();
  // A first-look listing is live for the seller (editable, in the Active tab); only its badge differs
  if (isFirstLookStatus(status)) return "Active";
  if (s === "OPEN" || s === "NEW" || s === "ACTIVE" || s === "VIEWED") return "Active";
  if (s === "PENDING" || s === "OFFER_SUBMITTED" || s === "OFFER SUBMITTED" || s === "IN_ESCROW" || s === "IN ESCROW")
    return "Pending";
  if (s === "SOLD" || s === "CLOSED") return "Sold";
  if (s === "CANCELLED" || s === "WITHDRAWN" || s === "EXPIRED") return "Withdrawn";
  return status ?? "";
};

/** Sellers may only edit listings that are still live */
export const isListingEditable = (status: string | null | undefined): boolean => {
  const group = normalizeListingStatus(status);
  return group === "Active" || group === "Pending";
};

/** "Updated 3 hours ago" / "Sold 2 days ago"; safe for missing or invalid dates */
export const formatRelativeTime = (date: Date | string | null | undefined, isSold = false): string => {
  const prefix = isSold ? "Sold" : "Updated";
  if (!date) return "";
  const then = new Date(date);
  if (Number.isNaN(then.getTime())) return "";

  const diffMs = Date.now() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMins < 1) return `${prefix} just now`;
  if (diffMins < 60) return `${prefix} ${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${prefix} ${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${prefix} ${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  if (diffWeeks < 4) return `${prefix} ${diffWeeks} week${diffWeeks !== 1 ? "s" : ""} ago`;
  return `${prefix} ${then.toLocaleDateString()}`;
};

/** Whole hours until a future deadline, or null when none / already passed */
export const getHoursUntilDeadline = (deadline: Date | string | null | undefined): number | null => {
  if (!deadline) return null;
  const diffMs = new Date(deadline).getTime() - Date.now();
  if (Number.isNaN(diffMs) || diffMs <= 0) return null;
  return Math.floor(diffMs / (1000 * 60 * 60));
};

/** "Closes in 5h" / "Closes in 2d" */
export const formatDeadlineCountdown = (hours: number): string => {
  if (hours < 1) return "Closes within the hour";
  if (hours < 48) return `Closes in ${hours}h`;
  return `Closes in ${Math.floor(hours / 24)}d`;
};

/** Value for a <input type="datetime-local"> in the viewer's own time zone */
export const toLocalDateTimeInput = (value: Date | string | null | undefined): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Local "YYYY-MM-DDTHH:mm" from a datetime-local input → ISO string with offset, or undefined */
export const localDateTimeToIso = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};
